export interface AddressGeocodeInput {
  street: string;
  housenumber: string;
  city: string;
}

export interface AddressGeocodeAddress {
  road?: string;
  pedestrian?: string;
  house_number?: string;
  city?: string;
  town?: string;
  village?: string;
  municipality?: string;
}

export interface AddressGeocodeResult {
  x: number;
  y: number;
  label: string;
  raw?: {
    address?: AddressGeocodeAddress;
  };
}

export interface AddressDisplayData {
  address: string;
  address_description: string;
  geolocation: unknown;
}

export function normalizeAddressValue(value: string): string {
  return value.toLowerCase().replace(/\s+/g, ' ').trim();
}

export function buildLocationAddress(location: { street?: string; housenumber?: string; zipcode?: string; city?: string } | undefined): string {
  if (!location) {
    return '';
  }

  const street = location.street ?? '';
  const housenumber = location.housenumber ?? '';
  const zipcode = location.zipcode ?? '';
  const city = location.city ?? '';

  const streetLine = [street, housenumber].filter(Boolean).join(' ').trim();
  const cityLine = [zipcode, city].filter(Boolean).join(' ').trim();

  return [streetLine, cityLine].filter(Boolean).join(', ');
}

export function labelContainsExactAddress(label: string, street: string, housenumber: string, city: string): boolean {
  const normalizedLabel = normalizeAddressValue(label);
  return (
    normalizedLabel.includes(street) &&
    normalizedLabel.includes(housenumber) &&
    (!city || normalizedLabel.includes(city))
  );
}

export function findExactAddressMatch(results: AddressGeocodeResult[], input: AddressGeocodeInput): AddressGeocodeResult | undefined {
  const street = normalizeAddressValue(input.street);
  const housenumber = normalizeAddressValue(input.housenumber);
  const city = normalizeAddressValue(input.city);

  return results.find((result) => {
    const address = result.raw?.address;

    if (!address) {
      return labelContainsExactAddress(result.label, street, housenumber, city);
    }

    const resultStreet = normalizeAddressValue(address.road ?? address.pedestrian ?? '');
    const resultHouseNumber = normalizeAddressValue(address.house_number ?? '');
    const resultCity = normalizeAddressValue(
      address.city ?? address.town ?? address.village ?? address.municipality ?? ''
    );

    return (
      resultStreet === street &&
      resultHouseNumber === housenumber &&
      (!city || !resultCity || resultCity === city)
    );
  });
}
